import AsyncStorage from '@react-native-async-storage/async-storage';
import moment, { Moment } from 'moment';
import {
  areTimeslotsEqual,
  AVAILABILITY_DATE_FORMAT,
  cartHasLeadTimeItem,
  findAvailabilityDay,
  getValidFulfillment,
  isFulfillmentStale,
  OrderAvailability,
  ValidFulfillmentValue,
} from '@domain/fulfillment-settings/blocs/order-availability';
import {
  AvailabilityDayFragment,
  AvailableOrderFulfillmentFieldsFragment,
  EmployeeRole,
  FulfillmentName,
  FulfillmentTimeslotSelection,
  NewOrder_OrderFieldsFragment,
  Orders_Set_Input,
} from '@/types';
import { captureException } from '@/utils/sentry';
import {
  EmployeeRestaurant,
  useAuthenticatedUserContext,
} from '@/contexts/user-context';
import {
  FulfillmentHookParams,
  FulfillmentHookValue,
  FulfillmentMap,
  SelectedFulfillmentType,
} from '@/contexts/new-order-actions-context-types';
import { getCartKeyForOrder } from '@/utils/new-order';
import {
  Timeslot,
  TimeZoneMap,
} from '@domain/fulfillment-settings/types/types';
import { useAvailableOrderFulfillments } from './orders/use-available-order-fulfillments';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDraftOrders } from './orders/use-draft-order';
import {
  useDynamicConfigValue,
  useGetDynamicConfigValueForChat,
  useSupplierDynamicConfigValue,
} from './use-dynamic-config-value';
import {
  useMaybeNewOrderContext,
  useNewOrderContext,
} from '@/contexts/new-order-context';

const FULFILLMENT_MAP_KEY = 'fulfillmentMap';

export const getValidTimeslotForAvailability = (
  availabilityDay: AvailabilityDayFragment,
  timeslot: Timeslot | null,
): Timeslot | null => {
  const timeslotSettings = availabilityDay?.timeslot_settings;
  // timeslot shouldn't be set if there are no settings
  if (!timeslotSettings) {
    return null;
  }

  // if default is none, null is a valid timeslot
  // if default is first available, we should continue
  const defaultNone =
    timeslotSettings.default_selection === FulfillmentTimeslotSelection.None;
  if (!timeslot && defaultNone) {
    return null;
  }

  const isTimeslotValid =
    timeslot &&
    timeslotSettings.timeslots.some(
      ({ start_time, end_time }) =>
        start_time === timeslot.startTime && end_time === timeslot.endTime,
    );
  if (isTimeslotValid) {
    return timeslot;
  }

  // saved timeslot is invalid. So use default selection to either
  // clear it, or select first available
  const firstAvailable = timeslotSettings.timeslots[0];
  if (defaultNone || !firstAvailable) {
    return null;
  }

  return {
    endTime: firstAvailable.end_time,
    startTime: firstAvailable.start_time,
  };
};

const getValidTimeslotForNextDate = (
  availabilityFields: AvailableOrderFulfillmentFieldsFragment,
  fulfillmentDateMoment: Moment,
  savedTimeslot: Timeslot | null,
): Timeslot | null => {
  const dateString = fulfillmentDateMoment.format(AVAILABILITY_DATE_FORMAT);
  const found = availabilityFields.availability.find(
    (av) => av.date === dateString,
  );
  if (!found) {
    return null;
  }
  return getValidTimeslotForAvailability(found, savedTimeslot);
};

interface GetFulfillmentKeyParams {
  draftOrder: NewOrder_OrderFieldsFragment | null;
  multiCartEnabled: boolean;
  restaurantUUID: string | null;
}

const getFulfillmentKey = ({
  draftOrder,
  multiCartEnabled,
  restaurantUUID,
}: GetFulfillmentKeyParams): string | null => {
  if (!restaurantUUID) {
    return null;
  }
  return multiCartEnabled
    ? `${restaurantUUID}:${getCartKeyForOrder(draftOrder)}`
    : restaurantUUID;
};

interface GetOrderFulfillmentParams {
  date: string | null | undefined;
  timeslotEnd: string | null | undefined;
  timeslotStart: string | null | undefined;
  type: string | null | undefined;
}

const getOrderFulfillment = ({
  date,
  timeslotEnd,
  timeslotStart,
  type,
}: GetOrderFulfillmentParams): SelectedFulfillmentType | null => {
  if (!type || !date) {
    return null;
  }
  return {
    date,
    fulfillmentSettingsUUID: null,
    timeslot:
      timeslotStart && timeslotEnd
        ? { endTime: timeslotEnd, startTime: timeslotStart }
        : null,
    type: type as FulfillmentName,
  };
};

interface GetNextSelectedFulfillmentParams {
  additionalOrderValidationRoles: EmployeeRole[];
  availableOrderFulfillments: AvailableOrderFulfillmentFieldsFragment[];
  savedSelectedFulfillment: SelectedFulfillmentType;
  supplierEmployeeRole: EmployeeRole | null;
  timezoneMap: TimeZoneMap;
}

const getNextSelectedFulfillment = ({
  additionalOrderValidationRoles,
  availableOrderFulfillments,
  savedSelectedFulfillment,
  supplierEmployeeRole,
  timezoneMap,
}: GetNextSelectedFulfillmentParams): ValidFulfillmentValue | null => {
  const savedFulfillmentOption = new OrderAvailability(
    availableOrderFulfillments,
    timezoneMap,
    savedSelectedFulfillment.type,
    true,
  ).selectedAvailability;

  if (!savedFulfillmentOption) {
    return null;
  }

  const validFulfillment = getValidFulfillment({
    additionalOverrideSupplierRoles: additionalOrderValidationRoles,
    availabilities: availableOrderFulfillments,
    savedFulfillmentDateMoment: moment(savedSelectedFulfillment.date),
    savedFulfillmentOption,
    supplierEmployeeRole,
    timezones: timezoneMap,
  });

  return isFulfillmentStale({
    savedFulfillmentDate: savedSelectedFulfillment.date,
    savedFulfillmentType: savedFulfillmentOption.fulfillment_name,
    validFulfillment,
  })
    ? validFulfillment
    : null;
};

const getNextAvailableFulfillment = (
  availableOrderFulfillments: AvailableOrderFulfillmentFieldsFragment[],
  timezoneMap: TimeZoneMap,
): SelectedFulfillmentType | null => {
  // No available fulfillments means there is nothing to hydrate yet.
  // Mark hydrated so consumers can proceed, but keep selected fulfillment null
  // and allow a future hydration pass when fulfillments become available.
  if (availableOrderFulfillments.length === 0) {
    return null;
  }
  const availability = new OrderAvailability(
    availableOrderFulfillments,
    timezoneMap,
    null,
  );
  const nextDay = availability.nextFulfillmentDayMoment;
  return {
    date: nextDay.toISOString(),
    fulfillmentSettingsUUID: availability.selectedFulfillmentSettingsUUID,
    timeslot: availability.selectedAvailability
      ? getValidTimeslotForNextDate(
          availability.selectedAvailability,
          nextDay,
          null,
        )
      : null,
    type: availability.fulfillmentType,
  };
};

export const useRestaurantScopedFulfillment = ({
  restaurantUUID,
}: {
  restaurantUUID: string;
}) => {
  const { restaurantUUIDToRelationshipMap, supplierMap, supplierEmployeeMap } =
    useAuthenticatedUserContext();
  const { getDynamicConfigValueForChat } = useGetDynamicConfigValueForChat();

  const restaurant = restaurantUUIDToRelationshipMap[restaurantUUID];

  const supplier = supplierMap[restaurant?.supplierUUID];
  const supplierTimezone =
    supplier?.address.timezone ??
    Intl.DateTimeFormat().resolvedOptions().timeZone;

  const supplierBusinessEmployee =
    supplierEmployeeMap[restaurant?.supplierUUID];
  const supplierEmployeeRole =
    supplierBusinessEmployee.employee_role as EmployeeRole;

  const additionalOrderValidationRolesConfig = getDynamicConfigValueForChat(
    restaurant.chatUUIDs[0],
    'additional_order_validation_supplier_roles',
  );

  const resetToDefaultFulfillment = getDynamicConfigValueForChat(
    restaurant.chatUUIDs[0],
    'reset_fulfillment_on_order_submission',
  );

  const orderContext = useMaybeNewOrderContext();
  const scopedActiveCart =
    orderContext?.multiCartEnabled &&
    orderContext.restaurantUUID === restaurantUUID
      ? {
          draftOrder: orderContext.draftOrder,
          hasResponse: orderContext.hasResponse,
        }
      : null;

  const { data: draftOrders, hasResponse: hasDraftOrdersHasResponse } =
    useDraftOrders({
      chatUUID: restaurant?.chatUUIDs[0],
      restaurantUUID,
      skip: Boolean(scopedActiveCart),
      supplierUUID: restaurant?.supplierUUID,
    });

  const {
    data: availableOrderFulfillments,
    hasResponse: hasAvailableOrderFulfillmentsHasResponse,
  } = useAvailableOrderFulfillments({
    restaurantUUID,
    supplierUUID: restaurant?.supplierUUID,
  });

  const draftOrder =
    (draftOrders?.getOrders?.length ?? 0) > 0
      ? (draftOrders?.getOrders[0].order ?? null)
      : null;
  return useInternalSelectedFulfillment({
    additionalOrderValidationRolesConfig,
    availableOrderFulfillments:
      availableOrderFulfillments?.getAvailableOrderFulfillments ?? [],
    draftOrder: scopedActiveCart ? scopedActiveCart.draftOrder : draftOrder,
    hasResponse:
      hasAvailableOrderFulfillmentsHasResponse &&
      (scopedActiveCart
        ? scopedActiveCart.hasResponse
        : hasDraftOrdersHasResponse),
    multiCartEnabled: Boolean(scopedActiveCart),
    resetToDefaultFulfillment: Boolean(resetToDefaultFulfillment),
    restaurant: restaurant ?? null,
    restaurantUUID,
    supplierEmployeeRole,
    supplierTimezone,
  });
};

export const useSelectedFulfillment = ({
  updateFulfillment,
}: FulfillmentHookParams) => {
  const {
    availableOrderFulfillments,
    draftOrder,
    hasResponse,
    multiCartEnabled,
    restaurantUUID,
    supplierTimezone,
  } = useNewOrderContext();
  const { selectedRestaurantRelationship, supplierEmployeeRole } =
    useAuthenticatedUserContext();

  const additionalOrderValidationRolesConfig = useSupplierDynamicConfigValue(
    'additional_order_validation_supplier_roles',
  );
  const resetToDefaultFulfillment = useDynamicConfigValue(
    'reset_fulfillment_on_order_submission',
  );

  return useInternalSelectedFulfillment({
    additionalOrderValidationRolesConfig,
    availableOrderFulfillments,
    draftOrder,
    hasResponse,
    multiCartEnabled,
    resetToDefaultFulfillment: Boolean(resetToDefaultFulfillment),
    restaurant: selectedRestaurantRelationship,
    restaurantUUID,
    supplierEmployeeRole,
    supplierTimezone,
    updateFulfillment,
  });
};

export const useInternalSelectedFulfillment = ({
  updateFulfillment,
  draftOrder,
  availableOrderFulfillments,
  restaurant,
  restaurantUUID,
  hasResponse,
  resetToDefaultFulfillment,
  supplierEmployeeRole,
  supplierTimezone,
  additionalOrderValidationRolesConfig,
  multiCartEnabled = false,
}: Partial<FulfillmentHookParams> & {
  additionalOrderValidationRolesConfig?: EmployeeRole[];
  availableOrderFulfillments: AvailableOrderFulfillmentFieldsFragment[];
  draftOrder: NewOrder_OrderFieldsFragment | null;
  hasResponse: boolean;
  multiCartEnabled?: boolean;
  resetToDefaultFulfillment: boolean;
  restaurant: EmployeeRestaurant | null;
  restaurantUUID: string | null;
  supplierEmployeeRole: EmployeeRole | null;
  supplierTimezone: string;
}) => {
  // use state to trigger re-renders, use ref to access values in effect hooks
  const [fulfillmentHydrated, setFulfillmentHydrated] = useState(false);
  const fulfillmentHydratedRef = useRef(false);
  const lastHydratedFulfillmentKeyRef = useRef<string>(null);
  const fulfillmentKey = getFulfillmentKey({
    draftOrder,
    multiCartEnabled,
    restaurantUUID,
  });
  const orderFulfillmentFallback = useMemo<SelectedFulfillmentType | null>(
    () =>
      multiCartEnabled
        ? getOrderFulfillment({
            date: draftOrder?.restaurant_desired_delivery_time,
            timeslotEnd: draftOrder?.fulfillment_timeslot_end,
            timeslotStart: draftOrder?.fulfillment_timeslot_start,
            type: draftOrder?.fulfillment_type,
          })
        : null,
    [
      multiCartEnabled,
      draftOrder?.fulfillment_type,
      draftOrder?.restaurant_desired_delivery_time,
      draftOrder?.fulfillment_timeslot_start,
      draftOrder?.fulfillment_timeslot_end,
    ],
  );

  const [fulfillmentMap, setFulfillmentMap] = useState<FulfillmentMap>({});
  const fulfillmentMapRef = useRef<FulfillmentMap>({});

  const availableOrderFulfillmentTypes = useMemo(() => {
    return new Set(availableOrderFulfillments.map((a) => a.fulfillment_name));
  }, [availableOrderFulfillments]);

  // Hydrate initial fulfillmentMap from AsyncStorage
  useEffect(() => {
    (async () => {
      try {
        const storedMap = await AsyncStorage.getItem(FULFILLMENT_MAP_KEY);
        const nextMap = storedMap ? JSON.parse(storedMap) : {};
        if (!fulfillmentHydratedRef.current) {
          setFulfillmentMap(nextMap);
          fulfillmentMapRef.current = nextMap;
        }
      } catch (e) {
        captureException(e);
      }
    })();
  }, []);

  // Save changes to fulfillmentMap to AsyncStorage
  useEffect(() => {
    (async () => {
      await AsyncStorage.setItem(
        FULFILLMENT_MAP_KEY,
        JSON.stringify(fulfillmentMap),
      );
    })();
  }, [fulfillmentMap]);

  // initial hydration for fulfillment date and type
  // only run once we know if there is a draft order response
  // and do not run once it has been manually changed
  useEffect(() => {
    if (!hasResponse) {
      return;
    }

    // Filter out entries from fulfillmentMap where the fulfillment types are
    // no longer available
    setFulfillmentMap((map) => {
      const fulfillmentEntries = Object.entries(map);
      const filteredFulfillmentEntries = fulfillmentEntries.filter(
        ([_, fulfillment]) => {
          return availableOrderFulfillmentTypes.has(fulfillment.type);
        },
      );

      // Return the existing map if none of the entries were filtered out to
      // prevent unnecessary re-renders
      if (filteredFulfillmentEntries.length === fulfillmentEntries.length) {
        return map;
      }

      const nextMap = Object.fromEntries(filteredFulfillmentEntries);
      fulfillmentMapRef.current = nextMap;
      return nextMap;
    });
  }, [availableOrderFulfillmentTypes, hasResponse]);

  const selectFulfillment = useCallback(
    (newFulfillment: SelectedFulfillmentType | null) => {
      if (!fulfillmentKey) {
        return;
      }

      setFulfillmentMap((map) => {
        const nextMap = { ...map };
        if (newFulfillment) {
          nextMap[fulfillmentKey] = newFulfillment;
        } else {
          delete nextMap[fulfillmentKey];
        }
        fulfillmentMapRef.current = nextMap;
        return nextMap;
      });
    },
    [fulfillmentKey],
  );

  const selectedFulfillment = fulfillmentKey
    ? (fulfillmentMap[fulfillmentKey] ?? orderFulfillmentFallback)
    : null;

  const timezoneMap = useMemo(() => {
    return {
      restaurant: restaurant?.timezone ?? moment.tz.guess(),
      supplier: supplierTimezone,
    };
  }, [restaurant?.timezone, supplierTimezone]);

  const nextAvailableFulfillment = useMemo(
    () => getNextAvailableFulfillment(availableOrderFulfillments, timezoneMap),
    [availableOrderFulfillments, timezoneMap],
  );

  // order submitted and cleared, or reset because of quantity.
  // verify saved fulfillment is still valid and only change if it is not
  // Skip if we manually reset the fulfillment to the next available when order
  // is submitted
  useEffect(() => {
    if (!hasResponse || !fulfillmentKey) {
      return;
    }

    const currentSelectedFulfillment =
      fulfillmentMapRef.current[fulfillmentKey];

    // Skip only when this cart was already hydrated, so a cart switch revalidates its saved date
    if (
      currentSelectedFulfillment &&
      lastHydratedFulfillmentKeyRef.current === fulfillmentKey
    ) {
      return;
    }

    if (availableOrderFulfillments.length === 0) {
      if (currentSelectedFulfillment) {
        selectFulfillment(null);
      }
      fulfillmentHydratedRef.current = true;
      lastHydratedFulfillmentKeyRef.current = fulfillmentKey;
      setFulfillmentHydrated(true);
      return;
    }

    let updateSetInput: Orders_Set_Input | null = null;

    if (draftOrder) {
      const savedTimeslotStart = draftOrder.fulfillment_timeslot_start;
      const savedTimeslotEnd = draftOrder.fulfillment_timeslot_end;
      const savedTimeslot: Timeslot | null =
        savedTimeslotStart && savedTimeslotEnd
          ? {
              endTime: savedTimeslotEnd,
              startTime: savedTimeslotStart,
            }
          : null;
      // use date and type from order to determine if date is valid
      const savedOrderAvailability = new OrderAvailability(
        availableOrderFulfillments,
        timezoneMap,
        draftOrder.fulfillment_type as FulfillmentName,
        true,
      );

      // Check if the draft order fulfillment is available, otherwise check the
      // saved fulfillment type, otherwise use the first available fulfillment
      const nextFulfillmentType =
        [
          draftOrder.fulfillment_type as FulfillmentName,
          fulfillmentMapRef.current[fulfillmentKey]?.type,
        ].find((fulfillmentType) =>
          availableOrderFulfillmentTypes.has(fulfillmentType),
        ) || availableOrderFulfillments[0]?.fulfillment_name;

      const savedFulfillmentOption =
        draftOrder.fulfillment_type === nextFulfillmentType
          ? savedOrderAvailability.selectedAvailability
          : null;
      const validFulfillment = savedFulfillmentOption
        ? getValidFulfillment({
            additionalOverrideSupplierRoles:
              additionalOrderValidationRolesConfig ?? [],
            availabilities: availableOrderFulfillments,
            hasLeadTimeItem: cartHasLeadTimeItem(draftOrder.orders_items),
            savedFulfillmentDateMoment:
              draftOrder.restaurant_desired_delivery_time
                ? moment(draftOrder.restaurant_desired_delivery_time)
                : null,
            savedFulfillmentOption,
            supplierEmployeeRole,
            timezones: timezoneMap,
          })
        : null;
      // CASE 1: Keep the draft order's date while some fulfillment setting
      // still serves it, otherwise move to the next available day. Whichever
      // setting ends up serving it supplies the type and the settings id.
      const nextFulfillmentOption =
        validFulfillment?.fulfillmentOption ??
        savedOrderAvailability.selectedAvailability;
      const nextFulfillmentDate =
        validFulfillment?.date ??
        savedOrderAvailability.nextFulfillmentDayMoment;
      const resolvedFulfillmentType =
        nextFulfillmentOption?.fulfillment_name ?? nextFulfillmentType;
      const nextTimeslot = nextFulfillmentOption
        ? getValidTimeslotForNextDate(
            nextFulfillmentOption,
            nextFulfillmentDate,
            savedTimeslot,
          )
        : null;

      const savedFulfillmentDate = draftOrder.restaurant_desired_delivery_time;
      const isDateMoved =
        !savedFulfillmentDate ||
        !nextFulfillmentDate.isSame(moment(savedFulfillmentDate));
      const isTypeChanged =
        resolvedFulfillmentType !== draftOrder.fulfillment_type;

      // A moved date rewrites all four fields. A kept date sends only what
      // actually changed, so an unchanged cart writes nothing.
      if (isDateMoved) {
        updateSetInput = {
          ...(updateSetInput || {}),
          fulfillment_timeslot_end: nextTimeslot?.endTime || null,
          fulfillment_timeslot_start: nextTimeslot?.startTime || null,
          fulfillment_type: resolvedFulfillmentType,
          restaurant_desired_delivery_time: nextFulfillmentDate.toISOString(),
        };
      } else if (
        isTypeChanged ||
        !areTimeslotsEqual(nextTimeslot, savedTimeslot)
      ) {
        updateSetInput = {
          ...(updateSetInput || {}),
          fulfillment_timeslot_end: nextTimeslot?.endTime || null,
          fulfillment_timeslot_start: nextTimeslot?.startTime || null,
          ...(isTypeChanged
            ? { fulfillment_type: resolvedFulfillmentType }
            : {}),
        };
      }

      selectFulfillment({
        date:
          !isDateMoved && savedFulfillmentDate
            ? savedFulfillmentDate
            : nextFulfillmentDate.toISOString(),
        fulfillmentSettingsUUID:
          nextFulfillmentOption?.fulfillment_settings_id ?? null,
        timeslot: nextTimeslot,
        type: resolvedFulfillmentType,
      });
    } else {
      const savedSelectedFulfillment =
        fulfillmentMapRef.current[fulfillmentKey];

      if (savedSelectedFulfillment) {
        const nextSelectedFulfillment = getNextSelectedFulfillment({
          additionalOrderValidationRoles:
            additionalOrderValidationRolesConfig ?? [],
          availableOrderFulfillments,
          savedSelectedFulfillment,
          supplierEmployeeRole,
          timezoneMap,
        });

        if (nextSelectedFulfillment) {
          // CASE 2: last selected fulfillment is no longer valid, so update it
          selectFulfillment({
            date: nextSelectedFulfillment.date.toISOString(),
            fulfillmentSettingsUUID:
              nextSelectedFulfillment.fulfillmentOption.fulfillment_settings_id,
            timeslot: getValidTimeslotForNextDate(
              nextSelectedFulfillment.fulfillmentOption,
              nextSelectedFulfillment.date,
              savedSelectedFulfillment.timeslot,
            ),
            type: nextSelectedFulfillment.fulfillmentOption.fulfillment_name,
          });
        }
      } else {
        const defaultNewOrderFulfillment = new OrderAvailability(
          availableOrderFulfillments,
          timezoneMap,
          null,
        );
        // CASE 3: nothing selected yet, so default to the next available day on the default type
        const nextFulfillmentDayMoment =
          defaultNewOrderFulfillment.nextFulfillmentDayMoment;
        const nextTimeslot = defaultNewOrderFulfillment.selectedAvailability
          ? getValidTimeslotForNextDate(
              defaultNewOrderFulfillment.selectedAvailability,
              nextFulfillmentDayMoment,
              null,
            )
          : null;
        selectFulfillment({
          date: defaultNewOrderFulfillment.nextFulfillmentDayMoment.toISOString(),
          fulfillmentSettingsUUID:
            defaultNewOrderFulfillment.selectedFulfillmentSettingsUUID,
          timeslot: nextTimeslot,
          type: defaultNewOrderFulfillment.fulfillmentType,
        });
      }
    }

    if (updateSetInput && updateFulfillment) {
      updateFulfillment(updateSetInput);
    }

    fulfillmentHydratedRef.current = true;
    lastHydratedFulfillmentKeyRef.current = fulfillmentKey;
    setFulfillmentHydrated(true);
  }, [
    additionalOrderValidationRolesConfig,
    availableOrderFulfillments,
    availableOrderFulfillmentTypes,
    resetToDefaultFulfillment,
    draftOrder,
    fulfillmentHydratedRef,
    fulfillmentKey,
    fulfillmentMapRef,
    hasResponse,
    multiCartEnabled,
    supplierEmployeeRole,
    restaurantUUID,
    restaurant?.timezone,
    selectFulfillment,
    setFulfillmentHydrated,
    supplierTimezone,
    timezoneMap,
    updateFulfillment,
  ]);

  const resetFulfillmentToNextAvailable = useCallback(() => {
    if (restaurantUUID && fulfillmentHydratedRef.current) {
      // no previously selected fulfillment, set to next available just to be safe
      const defaultFulfillment = new OrderAvailability(
        availableOrderFulfillments,
        timezoneMap,
        null,
      );
      selectFulfillment({
        date: defaultFulfillment.nextFulfillmentDayMoment.toISOString(),
        fulfillmentSettingsUUID:
          defaultFulfillment.selectedFulfillmentSettingsUUID,
        timeslot: null,
        type: defaultFulfillment.fulfillmentType,
      });
    }
  }, [
    availableOrderFulfillments,
    restaurantUUID,
    selectFulfillment,
    timezoneMap,
  ]);

  useEffect(() => {
    if (resetToDefaultFulfillment) {
      return;
    }

    if (availableOrderFulfillments.length === 0) {
      if (fulfillmentKey && fulfillmentMapRef.current[fulfillmentKey]) {
        selectFulfillment(null);
      }
      return;
    }

    if (!draftOrder && fulfillmentHydratedRef.current && fulfillmentKey) {
      const previousFulfillment = fulfillmentMapRef.current[fulfillmentKey];
      if (previousFulfillment) {
        const nextSelectedFulfillment = getNextSelectedFulfillment({
          additionalOrderValidationRoles:
            additionalOrderValidationRolesConfig ?? [],
          availableOrderFulfillments,
          savedSelectedFulfillment: previousFulfillment,
          supplierEmployeeRole,
          timezoneMap,
        });

        if (nextSelectedFulfillment) {
          selectFulfillment({
            date: nextSelectedFulfillment.date.toISOString(),
            fulfillmentSettingsUUID:
              nextSelectedFulfillment.fulfillmentOption.fulfillment_settings_id,
            timeslot: null,
            type: nextSelectedFulfillment.fulfillmentOption.fulfillment_name,
          });
        }
      } else {
        const defaultFulfillment = new OrderAvailability(
          availableOrderFulfillments,
          timezoneMap,
          null,
        );
        selectFulfillment({
          date: defaultFulfillment.nextFulfillmentDayMoment.toISOString(),
          fulfillmentSettingsUUID:
            defaultFulfillment.selectedFulfillmentSettingsUUID,
          timeslot: null,
          type: defaultFulfillment.fulfillmentType,
        });
      }
    }
  }, [
    additionalOrderValidationRolesConfig,
    availableOrderFulfillments,
    draftOrder,
    fulfillmentHydratedRef,
    fulfillmentKey,
    fulfillmentMapRef,
    resetToDefaultFulfillment,
    restaurantUUID,
    supplierEmployeeRole,
    selectFulfillment,
    timezoneMap,
  ]);

  return useMemo<FulfillmentHookValue>(
    () => ({
      fulfillmentHydrated,
      nextAvailableFulfillment,
      resetFulfillmentToNextAvailable,
      selectedFulfillment,
      selectFulfillment,
    }),
    [
      fulfillmentHydrated,
      nextAvailableFulfillment,
      resetFulfillmentToNextAvailable,
      selectedFulfillment,
      selectFulfillment,
    ],
  );
};

export const useSelectedFulfillmentWithTimeslotCutoffs = ({
  updateFulfillment,
}: FulfillmentHookParams) => {
  const {
    availableOrderFulfillments,
    draftOrder,
    hasResponse,
    multiCartEnabled,
    restaurantUUID,
    supplierTimezone,
  } = useNewOrderContext();
  const { selectedRestaurantRelationship, supplierEmployeeRole } =
    useAuthenticatedUserContext();

  const additionalOrderValidationRolesConfig = useSupplierDynamicConfigValue(
    'additional_order_validation_supplier_roles',
  );

  const resetToDefaultFulfillment = useDynamicConfigValue(
    'reset_fulfillment_on_order_submission',
  );

  return useInternalSelectedFulfillmentWithTimeslotCutoffs({
    additionalOrderValidationRolesConfig,
    availableOrderFulfillments,
    draftOrder,
    hasResponse,
    multiCartEnabled,
    resetToDefaultFulfillment: Boolean(resetToDefaultFulfillment),
    restaurant: selectedRestaurantRelationship,
    restaurantUUID,
    supplierEmployeeRole,
    supplierTimezone,
    updateFulfillment,
  });
};

interface GetSelectedFulfillmentTypeParams {
  availableOrderFulfillments: AvailableOrderFulfillmentFieldsFragment[];
  draftOrder: NewOrder_OrderFieldsFragment | null;
  savedFulfillmentType: FulfillmentName | null;
}

interface SelectedFulfillmentTypeValue {
  fulfillmentType: AvailableOrderFulfillmentFieldsFragment | null;
  matchesSavedFulfillmentType: boolean;
}

/*
 * Hydration logic for selected fulfillment type
 * - If there is a draft order, and the type is still available, use the draft order type
 * - If there's a selection saved in local storage, and that's still available, use that
 * - Otherwise, use the first available fulfillment type returned by the API
 */
const getSelectedFulfillmentType = ({
  availableOrderFulfillments,
  draftOrder,
  savedFulfillmentType,
}: GetSelectedFulfillmentTypeParams): SelectedFulfillmentTypeValue => {
  if (draftOrder) {
    const foundFulfillment = availableOrderFulfillments.find(
      (a) =>
        a.fulfillment_name === (draftOrder.fulfillment_type as FulfillmentName),
    );
    if (foundFulfillment) {
      return {
        fulfillmentType: foundFulfillment,
        matchesSavedFulfillmentType: true,
      };
    }
  }

  if (savedFulfillmentType) {
    const foundFulfillment = availableOrderFulfillments.find(
      (a) => a.fulfillment_name === savedFulfillmentType,
    );
    if (foundFulfillment) {
      return {
        fulfillmentType: foundFulfillment,
        matchesSavedFulfillmentType: !draftOrder,
      };
    }
  }

  return {
    fulfillmentType: availableOrderFulfillments[0] ?? null,
    matchesSavedFulfillmentType: false,
  };
};

interface GetFulfillmentDateParams {
  additionalOrderValidationRoles: EmployeeRole[];
  availability: OrderAvailability;
  draftOrder: NewOrder_OrderFieldsFragment | null;
  employeeRole: EmployeeRole | null;
  ignoreSavedFulfillmentDate: boolean;
  savedFulfillmentDate: string | null;
}

interface FulfillmentDateValue {
  availabilityDay: AvailabilityDayFragment | null;
  fulfillmentDate: string;
  fulfillmentOption: AvailableOrderFulfillmentFieldsFragment | null;
  matchesSavedFulfillmentDate: boolean;
}

/*
 * Hydration logic for selected fulfillment date
 * - If there's a draft order, and the date is still available, use the draft order date
 * - If there's a selection saved in local storage that's still available, AND there's no draft order use saved date
 * - Otherwise, use the next available fulfillment date
 *
 * All date validations are run using the users role to allow admins to override fulfillment dates and cutoff times
 *
 * Returns the AvailabilityDayFragment assocaited with the day for timeslot
 * validation. If the date is only valid for an admin, availabilityDay will be null
 */
const getFulfillmentDate = ({
  availability,
  draftOrder,
  savedFulfillmentDate,
  employeeRole,
  additionalOrderValidationRoles,
  ignoreSavedFulfillmentDate,
}: GetFulfillmentDateParams): FulfillmentDateValue => {
  const savedFulfillmentDateString = ignoreSavedFulfillmentDate
    ? null
    : draftOrder
      ? draftOrder.restaurant_desired_delivery_time
      : savedFulfillmentDate;
  const savedFulfillmentOption = availability.selectedAvailability;

  if (savedFulfillmentOption) {
    const validFulfillment = getValidFulfillment({
      additionalOverrideSupplierRoles: additionalOrderValidationRoles,
      availabilities: availability.availabilities,
      hasLeadTimeItem: cartHasLeadTimeItem(draftOrder?.orders_items ?? []),
      savedFulfillmentDateMoment: savedFulfillmentDateString
        ? moment(savedFulfillmentDateString)
        : null,
      savedFulfillmentOption,
      supplierEmployeeRole: employeeRole,
      timezones: availability.timezones,
    });

    return {
      availabilityDay: findAvailabilityDay(
        validFulfillment.fulfillmentOption,
        validFulfillment.date,
      ),
      fulfillmentDate: validFulfillment.date.toISOString(),
      fulfillmentOption: validFulfillment.fulfillmentOption,
      matchesSavedFulfillmentDate: Boolean(
        savedFulfillmentDateString &&
          validFulfillment.date.isSame(moment(savedFulfillmentDateString)),
      ),
    };
  }

  const nextFulfillmentDayAvailability =
    availability.nextFulfillmentDayAvailability;
  const nextFulfillmentDayMoment = nextFulfillmentDayAvailability
    ? availability.getMomentForAvailabilityDay(nextFulfillmentDayAvailability)
    : availability.nextFulfillmentDayMoment;

  return {
    availabilityDay: nextFulfillmentDayAvailability ?? null,
    fulfillmentDate: nextFulfillmentDayMoment.toISOString(),
    fulfillmentOption: null,
    matchesSavedFulfillmentDate: false,
  };
};

interface GetFulfillmentTimeslotParams {
  additionalOverrideSupplierRoles: EmployeeRole[];
  availability: OrderAvailability;
  availabilityDay: AvailabilityDayFragment | null;
  draftOrder: NewOrder_OrderFieldsFragment | null;
  ignoreSavedTimeslot: boolean;
  savedTimeslot: Timeslot | null;
  supplierEmployeeRole: EmployeeRole | null;
}

interface FulfillmentTimeslotValue {
  matchesSavedTimeslot: boolean;
  timeslot: Timeslot | null;
}

const getFulfillmentTimeslot = ({
  availabilityDay,
  draftOrder,
  ignoreSavedTimeslot,
  savedTimeslot,
  availability,
  supplierEmployeeRole,
  additionalOverrideSupplierRoles,
}: GetFulfillmentTimeslotParams): FulfillmentTimeslotValue => {
  const draftOrderTimeslot =
    draftOrder &&
    draftOrder.fulfillment_timeslot_start &&
    draftOrder.fulfillment_timeslot_end
      ? {
          endTime: draftOrder.fulfillment_timeslot_end,
          startTime: draftOrder.fulfillment_timeslot_start,
        }
      : null;

  const isPreviousTimeslotNull = draftOrder
    ? draftOrderTimeslot === null
    : savedTimeslot === null;

  // if there is no AvailabilityDay, or no timeslots for the day, return null
  // and report no changes if it was already null
  if (!availabilityDay || !availabilityDay.timeslot_settings) {
    return {
      matchesSavedTimeslot: isPreviousTimeslotNull,
      timeslot: null,
    };
  }

  // determine default timeslot to use if no valid timeslot is provided
  const defaultTimeslot =
    availabilityDay.timeslot_settings?.default_selection ===
    FulfillmentTimeslotSelection.None
      ? null
      : availability.getFirstAvailableTimeslot(availabilityDay);

  const defaultTimeslotResult = {
    matchesSavedTimeslot: false,
    timeslot: defaultTimeslot
      ? {
          endTime: defaultTimeslot.end_time,
          startTime: defaultTimeslot.start_time,
        }
      : null,
  };

  // If fulfillment day or type has already changed, use default timeslot
  if (ignoreSavedTimeslot) {
    // saved timeslots do not match. Fall back to default timeslot
    return defaultTimeslotResult;
  }

  // fulfillment day and type are the same, use existing draft order's timeslot,
  // or locally saved timeslot to validate
  const timeslotToValidate = draftOrder ? draftOrderTimeslot : savedTimeslot;

  if (
    timeslotToValidate &&
    availability.isValidTimeslotForAvailabilityDay(
      availabilityDay,
      timeslotToValidate,
      supplierEmployeeRole,
      additionalOverrideSupplierRoles,
    )
  ) {
    return {
      matchesSavedTimeslot: true,
      timeslot: timeslotToValidate,
    };
  }

  return defaultTimeslotResult;
};

export const useInternalSelectedFulfillmentWithTimeslotCutoffs = ({
  updateFulfillment,
  draftOrder,
  availableOrderFulfillments,
  restaurant,
  restaurantUUID,
  hasResponse,
  resetToDefaultFulfillment,
  supplierEmployeeRole,
  supplierTimezone,
  additionalOrderValidationRolesConfig,
  multiCartEnabled = false,
}: Partial<FulfillmentHookParams> & {
  additionalOrderValidationRolesConfig?: EmployeeRole[];
  availableOrderFulfillments: AvailableOrderFulfillmentFieldsFragment[];
  draftOrder: NewOrder_OrderFieldsFragment | null;
  hasResponse: boolean;
  multiCartEnabled?: boolean;
  resetToDefaultFulfillment: boolean;
  restaurant: EmployeeRestaurant | null;
  restaurantUUID: string | null;
  supplierEmployeeRole: EmployeeRole | null;
  supplierTimezone: string;
}) => {
  // use state to trigger re-renders, use ref to access values in effect hooks
  const [fulfillmentHydrated, setFulfillmentHydrated] = useState(false);
  const fulfillmentHydratedRef = useRef(false);
  const lastHydratedFulfillmentKeyRef = useRef<string>(null);
  const fulfillmentKey = getFulfillmentKey({
    draftOrder,
    multiCartEnabled,
    restaurantUUID,
  });
  const orderFulfillmentFallback = useMemo<SelectedFulfillmentType | null>(
    () =>
      multiCartEnabled
        ? getOrderFulfillment({
            date: draftOrder?.restaurant_desired_delivery_time,
            timeslotEnd: draftOrder?.fulfillment_timeslot_end,
            timeslotStart: draftOrder?.fulfillment_timeslot_start,
            type: draftOrder?.fulfillment_type,
          })
        : null,
    [
      multiCartEnabled,
      draftOrder?.fulfillment_type,
      draftOrder?.restaurant_desired_delivery_time,
      draftOrder?.fulfillment_timeslot_start,
      draftOrder?.fulfillment_timeslot_end,
    ],
  );

  const [fulfillmentMap, setFulfillmentMap] = useState<FulfillmentMap>({});
  const [fulfillmentMapLoaded, setFulfillmentMapLoaded] = useState(false);
  const fulfillmentMapRef = useRef<FulfillmentMap>({});

  const availableOrderFulfillmentTypes = useMemo(() => {
    return new Set(availableOrderFulfillments.map((a) => a.fulfillment_name));
  }, [availableOrderFulfillments]);

  // Hydrate initial fulfillmentMap from AsyncStorage
  useEffect(() => {
    (async () => {
      try {
        const storedMap = await AsyncStorage.getItem(FULFILLMENT_MAP_KEY);
        const nextMap = storedMap ? JSON.parse(storedMap) : {};
        if (!fulfillmentHydratedRef.current) {
          setFulfillmentMap(nextMap);
          fulfillmentMapRef.current = nextMap;
        }
      } catch (e) {
        captureException(e);
      } finally {
        setFulfillmentMapLoaded(true);
      }
    })();
  }, []);

  // Save changes to fulfillmentMap to AsyncStorage
  useEffect(() => {
    (async () => {
      await AsyncStorage.setItem(
        FULFILLMENT_MAP_KEY,
        JSON.stringify(fulfillmentMap),
      );
    })();
  }, [fulfillmentMap]);

  useEffect(() => {
    if (!hasResponse) {
      return;
    }

    // Filter out entries from fulfillmentMap where the fulfillment types are
    // no longer available
    setFulfillmentMap((map) => {
      const fulfillmentEntries = Object.entries(map);
      const filteredFulfillmentEntries = fulfillmentEntries.filter(
        ([_, fulfillment]) => {
          return availableOrderFulfillmentTypes.has(fulfillment.type);
        },
      );

      // Return the existing map if none of the entries were filtered out to
      // prevent unnecessary re-renders
      if (filteredFulfillmentEntries.length === fulfillmentEntries.length) {
        return map;
      }

      const nextMap = Object.fromEntries(filteredFulfillmentEntries);
      fulfillmentMapRef.current = nextMap;
      return nextMap;
    });
  }, [availableOrderFulfillmentTypes, hasResponse]);

  const selectFulfillment = useCallback(
    (newFulfillment: SelectedFulfillmentType | null) => {
      if (!fulfillmentKey) {
        return;
      }
      setFulfillmentMap((map) => {
        const nextMap = { ...map };
        if (newFulfillment) {
          nextMap[fulfillmentKey] = newFulfillment;
        } else {
          delete nextMap[fulfillmentKey];
        }
        fulfillmentMapRef.current = nextMap;
        return nextMap;
      });
    },
    [fulfillmentKey],
  );

  const selectedFulfillment = fulfillmentKey
    ? (fulfillmentMap[fulfillmentKey] ?? orderFulfillmentFallback)
    : null;

  const timezoneMap = useMemo(() => {
    return {
      restaurant: restaurant?.timezone ?? moment.tz.guess(),
      supplier: supplierTimezone,
    };
  }, [restaurant?.timezone, supplierTimezone]);

  const nextAvailableFulfillment = useMemo(
    () => getNextAvailableFulfillment(availableOrderFulfillments, timezoneMap),
    [availableOrderFulfillments, timezoneMap],
  );

  // initial hydration for fulfillment date and type
  // only run once we know if there is a draft order response
  // and do not run once it has been manually changed
  useEffect(() => {
    if (
      !hasResponse ||
      !restaurantUUID ||
      !fulfillmentMapLoaded ||
      !fulfillmentKey
    ) {
      return;
    }

    const currentSelectedFulfillment =
      fulfillmentMapRef.current[fulfillmentKey];

    // Skip only when this cart was already hydrated, so a cart switch revalidates its saved date
    if (
      currentSelectedFulfillment &&
      lastHydratedFulfillmentKeyRef.current === fulfillmentKey
    ) {
      return;
    }

    const {
      fulfillmentType: nextSelectedFulfillmentType,
      matchesSavedFulfillmentType,
    } = getSelectedFulfillmentType({
      availableOrderFulfillments,
      draftOrder,
      savedFulfillmentType: fulfillmentMapRef.current[fulfillmentKey]?.type,
    });

    // No available fulfillments. Select next day delivery as a fallback
    if (!nextSelectedFulfillmentType) {
      const defaultOrderAvailability = new OrderAvailability(
        availableOrderFulfillments,
        timezoneMap,
        null,
      );
      const defaultFulfillmentDate =
        defaultOrderAvailability.nextFulfillmentDayMoment.toISOString();
      const defaultFulfillmentType = defaultOrderAvailability.fulfillmentType;
      selectFulfillment({
        date: defaultFulfillmentDate,
        fulfillmentSettingsUUID:
          defaultOrderAvailability.selectedFulfillmentSettingsUUID,
        timeslot: null,
        type: defaultFulfillmentType,
      });

      if (updateFulfillment) {
        updateFulfillment({
          fulfillment_timeslot_end: null,
          fulfillment_timeslot_start: null,
          fulfillment_type: defaultFulfillmentType,
          restaurant_desired_delivery_time: moment(
            defaultFulfillmentDate,
          ).toISOString(),
        });
      }

      fulfillmentHydratedRef.current = true;
      lastHydratedFulfillmentKeyRef.current = fulfillmentKey;
      setFulfillmentHydrated(true);
      return;
    }

    const orderAvailability = new OrderAvailability(
      availableOrderFulfillments,
      timezoneMap,
      nextSelectedFulfillmentType.fulfillment_name,
      true,
    );

    const {
      availabilityDay: availabilityForFulfillmentDate,
      fulfillmentDate: nextSelectedFulfillmentDate,
      fulfillmentOption: resolvedFulfillmentOption,
      matchesSavedFulfillmentDate,
    } = getFulfillmentDate({
      additionalOrderValidationRoles:
        additionalOrderValidationRolesConfig ?? [],
      availability: orderAvailability,
      draftOrder,
      employeeRole: supplierEmployeeRole,
      ignoreSavedFulfillmentDate: !matchesSavedFulfillmentType,
      savedFulfillmentDate: fulfillmentMapRef.current[fulfillmentKey]?.date,
    });
    const resolvedFulfillmentType =
      resolvedFulfillmentOption?.fulfillment_name ??
      nextSelectedFulfillmentType.fulfillment_name;

    const { timeslot: nextSelectedTimeslot, matchesSavedTimeslot } =
      getFulfillmentTimeslot({
        additionalOverrideSupplierRoles:
          additionalOrderValidationRolesConfig ?? [],
        availability: orderAvailability,
        availabilityDay: availabilityForFulfillmentDate,
        draftOrder,
        ignoreSavedTimeslot:
          !matchesSavedFulfillmentDate || !matchesSavedFulfillmentType,
        savedTimeslot: fulfillmentMapRef.current[fulfillmentKey]?.timeslot,
        supplierEmployeeRole,
      });

    selectFulfillment({
      date: nextSelectedFulfillmentDate,
      fulfillmentSettingsUUID:
        resolvedFulfillmentOption?.fulfillment_settings_id ??
        orderAvailability.selectedFulfillmentSettingsUUID,
      timeslot: nextSelectedTimeslot,
      type: resolvedFulfillmentType,
    });

    const hasChanges =
      !matchesSavedTimeslot ||
      !matchesSavedFulfillmentDate ||
      !matchesSavedFulfillmentType ||
      resolvedFulfillmentType !== nextSelectedFulfillmentType.fulfillment_name;

    if (hasChanges && updateFulfillment) {
      updateFulfillment({
        fulfillment_timeslot_end: nextSelectedTimeslot?.endTime || null,
        fulfillment_timeslot_start: nextSelectedTimeslot?.startTime || null,
        fulfillment_type: resolvedFulfillmentType,
        restaurant_desired_delivery_time: moment(
          nextSelectedFulfillmentDate,
        ).toISOString(),
      });
    }
    fulfillmentHydratedRef.current = true;
    lastHydratedFulfillmentKeyRef.current = fulfillmentKey;
    setFulfillmentHydrated(true);
  }, [
    fulfillmentMapLoaded,
    additionalOrderValidationRolesConfig,
    availableOrderFulfillments,
    availableOrderFulfillmentTypes,
    draftOrder,
    fulfillmentHydratedRef,
    fulfillmentKey,
    fulfillmentMapRef,
    hasResponse,
    multiCartEnabled,
    supplierEmployeeRole,
    restaurantUUID,
    restaurant?.timezone,
    selectFulfillment,
    setFulfillmentHydrated,
    supplierTimezone,
    timezoneMap,
    updateFulfillment,
  ]);

  const resetFulfillmentToNextAvailable = useCallback(() => {
    if (restaurantUUID && fulfillmentHydratedRef.current) {
      const defaultFulfillment = new OrderAvailability(
        availableOrderFulfillments,
        timezoneMap,
        null,
      );

      const nextAvailabilityDay =
        defaultFulfillment.nextFulfillmentDayAvailability;

      const { timeslot } = getFulfillmentTimeslot({
        additionalOverrideSupplierRoles:
          additionalOrderValidationRolesConfig ?? [],
        availability: defaultFulfillment,
        availabilityDay: nextAvailabilityDay,
        draftOrder: null,
        ignoreSavedTimeslot: true,
        savedTimeslot: null,
        supplierEmployeeRole,
      });

      selectFulfillment({
        date: defaultFulfillment.nextFulfillmentDayMoment.toISOString(),
        fulfillmentSettingsUUID:
          defaultFulfillment.selectedFulfillmentSettingsUUID,
        timeslot,
        type: defaultFulfillment.fulfillmentType,
      });
    }
  }, [
    additionalOrderValidationRolesConfig,
    availableOrderFulfillments,
    supplierEmployeeRole,
    restaurantUUID,
    selectFulfillment,
    timezoneMap,
  ]);

  // order submitted and cleared, or reset because of quantity.
  // verify saved fulfillment is still valid and only change if it is not
  // Skip if we manually reset the fulfillment to the next available when order
  // is submitted
  useEffect(() => {
    if (resetToDefaultFulfillment) {
      return;
    }

    if (!draftOrder && fulfillmentHydratedRef.current && fulfillmentKey) {
      const previousFulfillment = fulfillmentMapRef.current[fulfillmentKey];
      if (previousFulfillment) {
        const nextSelectedFulfillment = getNextSelectedFulfillment({
          additionalOrderValidationRoles:
            additionalOrderValidationRolesConfig ?? [],
          availableOrderFulfillments,
          savedSelectedFulfillment: previousFulfillment,
          supplierEmployeeRole,
          timezoneMap,
        });

        if (nextSelectedFulfillment) {
          selectFulfillment({
            date: nextSelectedFulfillment.date.toISOString(),
            fulfillmentSettingsUUID:
              nextSelectedFulfillment.fulfillmentOption.fulfillment_settings_id,
            timeslot: null,
            type: nextSelectedFulfillment.fulfillmentOption.fulfillment_name,
          });
        }
      } else {
        // no previously selected fulfillment, set to next available just to be safe
        const defaultFulfillment = new OrderAvailability(
          availableOrderFulfillments,
          timezoneMap,
          null,
        );

        const nextAvailabilityDay =
          defaultFulfillment.nextFulfillmentDayAvailability;

        const { timeslot } = getFulfillmentTimeslot({
          additionalOverrideSupplierRoles:
            additionalOrderValidationRolesConfig ?? [],
          availability: defaultFulfillment,
          availabilityDay: nextAvailabilityDay,
          draftOrder: null,
          ignoreSavedTimeslot: true,
          savedTimeslot: null,
          supplierEmployeeRole,
        });

        selectFulfillment({
          date: defaultFulfillment.nextFulfillmentDayMoment.toISOString(),
          fulfillmentSettingsUUID:
            defaultFulfillment.selectedFulfillmentSettingsUUID,
          timeslot,
          type: defaultFulfillment.fulfillmentType,
        });
      }
    }
  }, [
    additionalOrderValidationRolesConfig,
    availableOrderFulfillments,
    draftOrder,
    fulfillmentHydratedRef,
    fulfillmentKey,
    fulfillmentMapRef,
    resetToDefaultFulfillment,
    restaurantUUID,
    supplierEmployeeRole,
    selectFulfillment,
    timezoneMap,
  ]);

  return useMemo<FulfillmentHookValue>(
    () => ({
      fulfillmentHydrated,
      nextAvailableFulfillment,
      resetFulfillmentToNextAvailable,
      selectedFulfillment,
      selectFulfillment,
    }),
    [
      fulfillmentHydrated,
      nextAvailableFulfillment,
      resetFulfillmentToNextAvailable,
      selectedFulfillment,
      selectFulfillment,
    ],
  );
};
